import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../api/api";

/* REUSABLE INPUT COMPONENTS */
const Field = ({ label, textarea, value, readOnly, onChange }) => (
  <div>
    <span className="label">{label}</span>
    {textarea ? (
      <textarea value={value || ""} readOnly={readOnly} onChange={onChange} />
    ) : (
      <input value={value || ""} readOnly={readOnly} onChange={onChange} />
    )}
  </div>
);

const Select = ({ label }) => (
  <div>
    <span className="label">{label}</span>
    <select>
      <option>Yes</option>
      <option>No</option>
    </select>
  </div>
);

const TabLink = ({ id, label, activeTab, onClick }) => (
  <div
    className={`tab-link ${activeTab === id ? "active" : ""}`}
    onClick={onClick}
  >
    {label}
  </div>
);

const TabPage = ({ id, title, children, activeTab }) =>
  activeTab === id && (
    <div className="tab-page">
      <div className="section-title">{title}</div>
      <div className="row">{children}</div>
    </div>
  );

function CustomerProfile() {
  const [activeTab, setActiveTab] = useState("basic");
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    id: "",
    full_name: "",
    mobile: "",
    whatsapp_number: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    total_orders: 0,
    last_service_date: "",
    favourite_provider: "",
    customer_rating: ""
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const token = localStorage.getItem("token");
      
      if (!user || !token) {
        navigate("/");
        return;
      }
      
      if (user.role !== "customer") {
        navigate("/partner-profile");
        return;
      }
      
      try {
        // Fetch fresh data from backend
        const response = await authAPI.getProfile(token);
        if (response.success) {
          const freshData = response.data;
          // Update localStorage with fresh data
          localStorage.setItem("user", JSON.stringify(freshData));
          setUserData(freshData);
          setFormData({
            id: freshData.id || "",
            full_name: freshData.full_name || "",
            mobile: freshData.mobile || "",
            whatsapp_number: freshData.whatsapp_number || "",
            email: freshData.email || "",
            address: freshData.address || "",
            city: freshData.city || "",
            state: freshData.state || "",
            pincode: freshData.pincode || "",
            total_orders: freshData.total_orders || 0,
            last_service_date: freshData.last_service_date || "",
            favourite_provider: freshData.favourite_provider || "",
            customer_rating: freshData.customer_rating || ""
          });
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        // Fallback to localStorage data
        setUserData(user);
        setFormData({
          id: user.id || "",
          full_name: user.full_name || "",
          mobile: user.mobile || "",
          whatsapp_number: user.whatsapp_number || "",
          email: user.email || "",
          address: user.address || "",
          city: user.city || "",
          state: user.state || "",
          pincode: user.pincode || "",
          total_orders: user.total_orders || 0,
          last_service_date: user.last_service_date || "",
          favourite_provider: user.favourite_provider || "",
          customer_rating: user.customer_rating || ""
        });
      }
    };
    
    fetchUserData();
  }, [navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdate = async (tabFields) => {
    setLoading(true);
    setMessage("");
    
    try {
      const token = localStorage.getItem("token");
      const dataToUpdate = {};
      
      // Only include fields from current tab
      tabFields.forEach(field => {
        if (formData[field] !== undefined) {
          dataToUpdate[field] = formData[field];
        }
      });

      const response = await authAPI.updateCustomer(dataToUpdate, token);
      
      if (response.success) {
        setMessage("✅ Updated successfully!");
        // Update localStorage
        const updatedUser = { ...userData, ...response.data };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUserData(updatedUser);
        setFormData(prev => ({ ...prev, ...response.data }));
        
        setTimeout(() => setMessage(""), 3000);
      }
    } catch (error) {
      setMessage("❌ " + (error.message || "Update failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-wrapper">

      {/* LEFT TABS */}
      <div className="tabs">
        <TabLink id="basic" label="Basic Information" activeTab={activeTab} onClick={() => setActiveTab("basic")} />
        <TabLink id="address" label="Address Details" activeTab={activeTab} onClick={() => setActiveTab("address")} />
        {/* <TabLink id="preferences" label="Service Preferences" activeTab={activeTab} onClick={() => setActiveTab("preferences")} /> */}
        <TabLink id="orders" label="Orders & History" activeTab={activeTab} onClick={() => setActiveTab("orders")} />
        {/* <TabLink id="support" label="Support" activeTab={activeTab} onClick={() => setActiveTab("support")} />
        <TabLink id="terms" label="Terms & Consent" activeTab={activeTab} onClick={() => setActiveTab("terms")} /> */}
      </div>

      {/* RIGHT CONTENT */}
      <div className="tab-content">
        {message && <div className="update-message">{message}</div>}

        <TabPage id="basic" title="Basic Information" activeTab={activeTab}>
          <Field 
            label="Full Name" 
            value={formData?.full_name || ""} 
            onChange={(e) => handleChange('full_name', e.target.value)}
          />
          <Field 
            label="Mobile Number" 
            value={formData?.mobile || ""} 
            onChange={(e) => handleChange('mobile', e.target.value)}
          />
          <Field 
            label="WhatsApp Number" 
            value={formData?.whatsapp_number || ""}
            onChange={(e) => handleChange('whatsapp_number', e.target.value)}
          />
          <Field 
            label="Email" 
            value={formData?.email || ""} 
            readOnly 
          />
          <button 
            className="update-btn" 
            onClick={() => handleUpdate(['full_name', 'mobile', 'whatsapp_number'])}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </TabPage>

        <TabPage id="address" title="Address Details" activeTab={activeTab}>
          <Field 
            label="Complete Address" 
            textarea 
            value={formData?.address || ""}
            onChange={(e) => handleChange('address', e.target.value)}
          />
          <Field 
            label="City" 
            value={formData?.city || ""}
            onChange={(e) => handleChange('city', e.target.value)}
          />
          <Field 
            label="State" 
            value={formData?.state || ""}
            onChange={(e) => handleChange('state', e.target.value)}
          />
          <Field 
            label="Pincode" 
            value={formData?.pincode || ""}
            onChange={(e) => handleChange('pincode', e.target.value)}
          />
          <button 
            className="update-btn" 
            onClick={() => handleUpdate(['address', 'city', 'state', 'pincode'])}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </TabPage>

        <TabPage id="orders" title="Orders & History" activeTab={activeTab}>
          <Field 
            label="Total Orders" 
            value={formData?.total_orders || "0"} 
            readOnly 
          />
          <Field 
            label="Last Service Date" 
            value={formData?.last_service_date || ""} 
            readOnly 
          />
          <Field 
            label="Favourite Service Provider" 
            value={formData?.favourite_provider || ""}
            onChange={(e) => handleChange('favourite_provider', e.target.value)}
          />
          <Field 
            label="Customer Rating Given" 
            value={formData?.customer_rating || ""}
            onChange={(e) => handleChange('customer_rating', e.target.value)}
          />
          <button 
            className="update-btn" 
            onClick={() => handleUpdate(['favourite_provider', 'customer_rating'])}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </TabPage>

        {/* <TabPage id="support" title="Support">
          <Field label="Support Contact Number" />
          <Field label="Support Email" />
          <Field label="Feedback / Complaint" textarea />
        </TabPage>

        <TabPage id="terms" title="Terms & Consent">
          <Select label="Accepts Terms & Conditions" />
          <Select label="Privacy Policy Accepted" />
          <Select label="Marketing Notifications Allowed" />
        </TabPage> */}

      </div>
    </div>
  );
}

export default CustomerProfile;
